import { useEffect, useMemo, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useRoles } from "@/context/RolesContext";
import {
  Wallet, FloppyDisk, Plus, Trash, ArrowClockwise, PencilSimple,
  CheckCircle, XCircle,
} from "@phosphor-icons/react";

const COLOR_SWATCHES = ["#002FA7", "#FF3B30", "#7C3AED", "#0A0A0A", "#F59E0B", "#10B981", "#EC4899", "#0EA5E9", "#6B7280"];

function slugify(label) {
  return (label || "")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

export default function ExpensesConfigTab() {
  const { roles: allRoles } = useRoles();
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState({ label: "", color: "#0A0A0A" });

  const load = async () => {
    try {
      const r = await api.get("/expenses/config");
      setCfg(r.data);
      setInitial(JSON.parse(JSON.stringify(r.data)));
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => cfg && initial && JSON.stringify(cfg) !== JSON.stringify(initial), [cfg, initial]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        default_currency: (cfg.default_currency || "USD").toUpperCase(),
        approver_role: cfg.approver_role,
        categories: cfg.categories.map(c => ({
          key: c.key,
          label: c.label,
          color: c.color || "#0A0A0A",
        })),
      };
      const r = await api.put("/expenses/config", payload);
      setCfg(r.data);
      setInitial(JSON.parse(JSON.stringify(r.data)));
      flash("ok", "Saved.");
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally { setSaving(false); }
  };

  const reset = () => setCfg(JSON.parse(JSON.stringify(initial)));

  const addCategory = () => {
    const label = newCat.label.trim();
    if (!label) return;
    const baseKey = slugify(label) || `cat_${Date.now()}`;
    let key = baseKey;
    const existing = new Set(cfg.categories.map(c => c.key));
    let n = 2;
    while (existing.has(key)) key = `${baseKey}_${n++}`;
    setCfg({ ...cfg, categories: [...cfg.categories, { key, label, color: newCat.color }] });
    setNewCat({ label: "", color: "#0A0A0A" });
  };

  const updateCategory = (idx, patch) => {
    const cats = [...cfg.categories];
    cats[idx] = { ...cats[idx], ...patch };
    setCfg({ ...cfg, categories: cats });
  };

  const removeCategory = (idx) => {
    if (cfg.categories.length <= 1) {
      flash("error", "At least one category is required.");
      return;
    }
    const cats = cfg.categories.filter((_, i) => i !== idx);
    setCfg({ ...cfg, categories: cats });
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const sortedRoles = [...allRoles].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-muted-foreground mb-6">
        Configure the expense tracker. Categories can be added/renamed at any time. The approver role
        determines who can approve submitted expenses (in addition to admin).
      </p>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border flex items-center gap-2 ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.type === "ok" ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
          {msg.text}
        </div>
      )}

      <section className="mb-8 border border-border p-5">
        <div className="eyebrow flex items-center gap-2 mb-3">
          <Wallet size={12} weight="bold" /> General
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow block mb-1">Default currency</label>
            <input
              type="text"
              value={cfg.default_currency}
              onChange={e => setCfg({ ...cfg, default_currency: e.target.value.toUpperCase() })}
              maxLength={6}
              className="w-full border border-border px-3 py-2 text-sm uppercase"
              data-testid="expenses-currency"
            />
            <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">
              ISO 4217 code (e.g. USD, EUR, INR, GBP). Used as the default when submitters don&apos;t pick one.
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-1">Approver role</label>
            <select
              value={cfg.approver_role}
              onChange={e => setCfg({ ...cfg, approver_role: e.target.value })}
              className="w-full border border-border px-3 py-2 text-sm"
              data-testid="expenses-approver-role"
            >
              {sortedRoles.map(r => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
            <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">
              Users with this role AND the <code>approve_expenses</code> capability see the approvals queue.
              Admin can always approve.
            </div>
          </div>
        </div>
      </section>

      <section className="mb-8 border border-border p-5">
        <div className="eyebrow mb-3">Categories ({cfg.categories.length})</div>

        <div className="space-y-2 mb-4">
          {cfg.categories.map((c, idx) => (
            <div key={c.key} className="flex items-center gap-2 border border-border p-2" data-testid={`cat-row-${c.key}`}>
              <span
                className="text-[10px] font-mono-mini px-1.5 py-0.5 text-white shrink-0"
                style={{ backgroundColor: c.color || "#0A0A0A" }}
              >
                {c.key}
              </span>
              <input
                type="text"
                value={c.label}
                onChange={e => updateCategory(idx, { label: e.target.value })}
                className="flex-1 border border-border px-2 py-1 text-sm"
                data-testid={`cat-label-${c.key}`}
              />
              <div className="flex items-center gap-1">
                {COLOR_SWATCHES.map(sw => (
                  <button
                    key={sw}
                    type="button"
                    onClick={() => updateCategory(idx, { color: sw })}
                    className={`w-6 h-6 border-2 ${c.color === sw ? "border-black" : "border-transparent"}`}
                    style={{ backgroundColor: sw }}
                    title={sw}
                  />
                ))}
              </div>
              <button
                onClick={() => removeCategory(idx)}
                className="border border-border p-1.5 hover:bg-muted"
                title="Remove"
              >
                <Trash size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-end gap-2 border-t border-border pt-3">
          <div className="flex-1">
            <label className="eyebrow block mb-1">Add category</label>
            <input
              type="text"
              value={newCat.label}
              onChange={e => setNewCat({ ...newCat, label: e.target.value })}
              placeholder="e.g. Marketing"
              className="w-full border border-border px-3 py-2 text-sm"
              data-testid="new-category-label"
            />
          </div>
          <div>
            <label className="eyebrow block mb-1">Color</label>
            <div className="flex items-center gap-1">
              {COLOR_SWATCHES.slice(0, 5).map(sw => (
                <button
                  key={sw}
                  type="button"
                  onClick={() => setNewCat({ ...newCat, color: sw })}
                  className={`w-7 h-7 border-2 ${newCat.color === sw ? "border-black" : "border-transparent"}`}
                  style={{ backgroundColor: sw }}
                />
              ))}
            </div>
          </div>
          <button
            onClick={addCategory}
            disabled={!newCat.label.trim()}
            className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-40 flex items-center gap-1"
            data-testid="add-category"
          >
            <Plus size={14} weight="bold" /> Add
          </button>
        </div>
      </section>

      <div className="flex items-center gap-2">
        <button onClick={reset} disabled={!dirty} className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-40 flex items-center gap-2">
          <ArrowClockwise size={14} weight="bold" /> Reset
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-40"
          data-testid="save-expenses-config"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
